import React, { useMemo, useState } from "react";
import "./HowItWorks.css";
import { useNavigate } from "react-router-dom";

import StepOne from "../../Assets/step-one.png";
import StepTwo from "../../Assets/step-two.png";
import StepThree from "../../Assets/step-three.png";
import StepFour from "../../Assets/step-four.png";
import StepFive from "../../Assets/step-five.png";

/* Tiny inline chevron (no dependency) */
const HiwChevron = ({ open }) => (
  <svg
    className={`hiw-chevron ${open ? "hiw-open" : ""}`}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      fill="currentColor"
      d="M7.4 8.6L12 13.2l4.6-4.6 1.4 1.4L12 16l-6-6 1.4-1.4z"
    />
  </svg>
);

const HowItWorks = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(0);

  const handleBuildChatbot = () => navigate("/BuildChatBot");
  const handleBookDemo = () => navigate("/bookdemo");

  const steps = useMemo(
    () => [
      {
        title: "Connect your content",
        content:
          "Add website links, PDFs, docs, spreadsheets, or sync from Drive / Confluence / Notion.",
        image: StepOne,
      },
      {
        title: "Create your assistant",
        content:
          "Choose the tone, welcome message, and what the assistant should prioritize.",
        image: StepTwo,
      },
      {
        title: "Add to your site",
        content:
          "Copy one snippet or embed in an iframe — works with any website in minutes.",
        image: StepThree,
      },
      {
        title: "Integrate your tools",
        content:
          "Send leads, requests, and updates into the systems your team already uses.",
        image: StepFour,
      },
      {
        title: "Review & improve",
        content:
          "See conversations, refine answers, and keep content fresh over time.",
        image: StepFive,
      },
    ],
    []
  );

  const total = steps.length;
  const progress = ((openIndex + 1) / total) * 100;

  const onSelect = (i) => setOpenIndex(i);

  return (
    <section className="hiw" aria-label="How it works">
      <div className="hiw__bg" aria-hidden="true">
        <div className="hiw__grid" />
        <div className="hiw__glow hiw__glow--a" />
        <div className="hiw__glow hiw__glow--b" />
      </div>

      <div className="hiw__wrap">
        <header className="hiw__head">
          <div className="hiw__kicker">Setup in minutes</div>

          <h2 className="hiw__title">
            <span className="hiw__titleSolid">How</span>{" "}
            <span className="hiw__titleGrad">it works</span>
          </h2>

          <p className="hiw__subtext">
            Upload content, configure the assistant, and add it to your website
            no complex setup.
          </p>

          

          <div className="hiw__progress" aria-label="setup progress">
            <div className="hiw__progressTrack">
              <div className="hiw__progressFill" style={{ width: `${progress}%` }} />
            </div>
            <span className="hiw__progressLabel">
              Step <b>{openIndex + 1}</b> / {total}
            </span>
          </div>
        </header>

        <div className="hiw__body">
          {/* LEFT: steps */}
          <div className="hiw__steps" role="tablist" aria-label="How it works steps">
            {steps.map((s, i) => {
              const open = openIndex === i;

              return (
                <div key={i} className={`hiw__step ${open ? "is-open" : ""}`}>
                  <button
                    className="hiw__stepBtn"
                    role="tab"
                    aria-selected={open}
                    aria-controls={`hiw-panel-${i}`}
                    id={`hiw-tab-${i}`}
                    onClick={() => onSelect(i)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(i);
                      }
                      if (e.key === "ArrowDown") onSelect((i + 1) % total);
                      if (e.key === "ArrowUp") onSelect((i - 1 + total) % total);
                    }}
                  >
                    <span className="hiw__badge">{i + 1}</span>

                    <span className="hiw__stepText">
                      <span className="hiw__stepTitle">{s.title}</span>
                      
                    </span>

                    <HiwChevron open={open} />
                  </button>

                  {/* optional: extra detail area only for open step */}
                  <div
                    id={`hiw-panel-${i}`}
                    role="region"
                    aria-labelledby={`hiw-tab-${i}`}
                    className="hiw__panel"
                    style={{ display: open ? "block" : "none" }}
                  >
                    <div className="hiw__panelLine" />
                    <p className="hiw__panelText">{s.content}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT: preview */}
          <div className="hiw__preview">
            <div className="hiw__device">
              <div className="hiw__deviceTop">
                <span className="hiw__dot" />
                <span className="hiw__dot" />
                <span className="hiw__dot" />
                <span className="hiw__pill">Preview</span>
              </div>

              <div className="hiw__shotWrap">
                <img
                  src={steps[openIndex].image}
                  alt={`Preview for: ${steps[openIndex].title}`}
                  className="hiw__shot"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="hiw__previewHint">
              Tip: keep content updated — your assistant improves over time.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
